import React, { isValidElement } from "react";
import { Icon } from "../../Icon/Icon";
import { Avatar } from "../../Avatar/Avatar";
import { Loading } from "../../Loading/Loading";
import { ButtonFlagsType, ButtonProps } from "../types";
import { SizeType } from "shared/src/types";
import { IconProps } from "components/src/components/Icon/types";
import cx from "components/src/utils/cx";
import { parseImage } from "shared/src/utils/parseImage";
import {
  iconOnlyFocusedDestructive,
  statusIcons,
} from "../styles/Button.classes";

const pillVariantIconSizes = {
  xs: "sm",
  sm: "md",
  md: "lg",
  lg: "xl",
};
const pillVariantAvatarSizes = {
  xs: "md",
  sm: "lg",
  md: "xl",
  lg: "2xl",
};
const smallAvatar: SizeType[] = ["2xs", "xs", "sm"];
const largeAvatar: SizeType[] = ["md", "lg", "xl"];

export const Spacer = ({ fluid }: Partial<ButtonProps>) =>
  fluid ? (
    <span className={"cui__spacer cui-fluid-block cui-flex-grow"} />
  ) : null;

export const RightIcon = ({
  size,
  rightIcon,
  fluid,
  FLAGS,
  variant,
}: Partial<ButtonProps> & { FLAGS: ButtonFlagsType }) => {
  if (FLAGS.iconOnly || !FLAGS.hasRightIcon) return null;
  let _icon: IconProps | string | null = null;
  if (rightIcon && typeof rightIcon === "string") _icon = { name: rightIcon };
  if (rightIcon && typeof rightIcon === "object" && "name" in rightIcon)
    _icon = rightIcon;
  const hasColor = !!_icon && !!_icon?.color;
  return (
    <>
      <Spacer fluid={fluid} />
      {_icon ? (
        <Icon
          {..._icon}
          size={
            variant === "pill" && size && pillVariantIconSizes[size]
              ? (pillVariantIconSizes[size] as SizeType)
              : size
          }
          className={cx({
            cui__color_icon: hasColor,
            "cui-translate-y-2": Boolean(FLAGS.canShowDescription) && !!fluid,
            ["className" in _icon ? `${_icon?.className}` : ""]: true,
          })}
        />
      ) : null}
    </>
  );
};

export const LeftIcon = ({
  status,
  fluid,
  size,
  leftIcon,
  variant,
  FLAGS,
}: Partial<ButtonProps> & { FLAGS: ButtonFlagsType }) => {
  const _size =
    variant === "pill" && size && pillVariantIconSizes[size]
      ? (pillVariantIconSizes[size] as SizeType)
      : size;

  const imageProps = Object.fromEntries(
    Object.entries({
      size: _size,
      color:
        typeof leftIcon === "object" && leftIcon?.color
          ? leftIcon.color
          : "",
    }).filter((v) => !!v)
  );
  const ParsedIcon = parseImage(leftIcon, imageProps);

  /** No Icon or status, return null */
  if (!FLAGS?.hasIcon && !FLAGS?.hasRightIcon && !FLAGS?.hasStatus) return null;

  /** Status Icon */
  let statusIcon: IconProps | string | null = null;
  if (status && status in statusIcons)
    statusIcon = { name: statusIcons[status] };
  if (statusIcon && !leftIcon && leftIcon !== null) return <Icon {...statusIcon} size={_size} />;

  /** Loading Spinner */
  const hasXsLoader = size === "sm" || size === "xs";
  if (FLAGS.hasStatus && status === "pending" && !leftIcon && leftIcon !== null)
    return <Loading variant="subtle" size={hasXsLoader ? "xs" : "sm"} />;

  /** Render leftIcon, or passed in valid ReactNode */
  return (
    <>
      {fluid && FLAGS.hasRightIcon ? <Spacer fluid={fluid} /> : null}
      {isValidElement(ParsedIcon) ? ParsedIcon : null}
    </>
  );
};
